/**
 * Admin System Info — Ducks pattern (actions, reducer).
 * Manages state for system version information display.
 */

import type { SystemInfo, SystemInfoState } from './types';

// ─── Action Type Constants ────────────────────────────────

export const FETCH_SYSTEM_INFO_REQUEST = 'FETCH_SYSTEM_INFO_REQUEST';
export const FETCH_SYSTEM_INFO_SUCCESS = 'FETCH_SYSTEM_INFO_SUCCESS';
export const FETCH_SYSTEM_INFO_FAILURE = 'FETCH_SYSTEM_INFO_FAILURE';

// ─── Action Creators ──────────────────────────────────────

export function fetchSystemInfoRequest() {
  return { type: FETCH_SYSTEM_INFO_REQUEST } as const;
}

export function fetchSystemInfoSuccess(data: SystemInfo) {
  return { type: FETCH_SYSTEM_INFO_SUCCESS, payload: data } as const;
}

export function fetchSystemInfoFailure(error: string) {
  return { type: FETCH_SYSTEM_INFO_FAILURE, payload: error } as const;
}

// ─── Action Union Type ────────────────────────────────────

type SystemInfoAction =
  | ReturnType<typeof fetchSystemInfoRequest>
  | ReturnType<typeof fetchSystemInfoSuccess>
  | ReturnType<typeof fetchSystemInfoFailure>;

// ─── Initial State ────────────────────────────────────────

export const initialState: SystemInfoState = {
  data: null,
  loading: false,
  error: null,
};

// ─── Reducer ──────────────────────────────────────────────

export function adminSystemInfoReducer(
  state: SystemInfoState = initialState,
  action: SystemInfoAction | { type: string; payload?: unknown }
): SystemInfoState {
  switch (action.type) {
    case FETCH_SYSTEM_INFO_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case FETCH_SYSTEM_INFO_SUCCESS: {
      const data = (action as ReturnType<typeof fetchSystemInfoSuccess>).payload;
      return {
        ...state,
        data,
        loading: false,
        error: null,
      };
    }

    case FETCH_SYSTEM_INFO_FAILURE: {
      const error = (action as ReturnType<typeof fetchSystemInfoFailure>).payload;
      return {
        ...state,
        loading: false,
        error,
      };
    }

    default:
      return state;
  }
}

export default adminSystemInfoReducer;
